<template>
  <div class="fs" :class="{ disabled, open, 'fs--sm': size === 'sm' }" ref="root">
    <button
      ref="btnRef"
      class="fs-btn"
      type="button"
      :disabled="disabled"
      :aria-label="ariaLabel || placeholder"
      @click="toggle"
    >
      <span v-if="icon" class="fs-icon" aria-hidden="true">{{ icon }}</span>
      <span class="fs-text" :class="{ placeholder: !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <span class="fs-chevron" aria-hidden="true">▾</span>
    </button>

    <Teleport to="body">
      <Transition :name="openUp ? 'fs-pop-up' : 'fs-pop'">
        <div
          v-if="open"
          ref="popRef"
          class="fs-pop fs-pop--fixed"
          :class="{ 'fs-pop--up': openUp }"
          :style="popStyle"
        >
          <div v-if="searchable" class="fs-search">
            <span class="fs-search-ico" aria-hidden="true">⌕</span>
            <input v-model="q" class="fs-search-input" :placeholder="searchPlaceholder" />
          </div>
          <div class="fs-list" :style="{ maxHeight: `${listMaxHeight}px` }">
            <button
              v-for="opt in filtered"
              :key="String(opt.value)"
              type="button"
              class="fs-item"
              :class="{ active: opt.value === modelValue, disabled: opt.disabled }"
              :disabled="opt.disabled"
              @click="pick(opt)"
            >
              <span class="fs-item-label">{{ opt.label }}</span>
              <span v-if="showValue" class="fs-item-value">{{ opt.value }}</span>
            </button>
            <div v-if="!filtered.length" class="fs-empty">无匹配项</div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: [String, Number, Boolean, null], default: null },
  options: { type: Array, default: () => [] }, // [{value,label}]
  placeholder: { type: String, default: '请选择' },
  ariaLabel: { type: String, default: '' },
  icon: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '搜索…' },
  showValue: { type: Boolean, default: false },
  size: { type: String, default: 'md' }, // md | sm
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const openUp = ref(false)
const q = ref('')
const root = ref(null)
const btnRef = ref(null)
const popRef = ref(null)
const popStyle = ref({})
const listMaxHeight = ref(260)

function isEmptySelectValue(v) {
  return v === null || v === undefined || v === ''
}

/** 空值表示「不过滤」，收起时显示 placeholder（字段名），不显示「全部」 */
const selectedLabel = computed(() => {
  if (isEmptySelectValue(props.modelValue)) return ''
  const hit = (props.options || []).find((o) => o.value === props.modelValue)
  return hit ? hit.label : ''
})

const filtered = computed(() => {
  const list = props.options || []
  const query = (q.value || '').trim().toLowerCase()
  if (!props.searchable || !query) return list
  return list.filter((o) => {
    const a = String(o.label || '').toLowerCase()
    const b = String(o.value ?? '').toLowerCase()
    return a.includes(query) || b.includes(query)
  })
})

function close() {
  open.value = false
  q.value = ''
}

function toggle() {
  if (props.disabled) return
  open.value = !open.value
  if (!open.value) q.value = ''
}

function pick(opt) {
  const v = typeof opt === 'object' && opt && 'value' in opt ? opt.value : opt
  if (typeof opt === 'object' && opt && opt.disabled) return
  emit('update:modelValue', v)
  emit('change', v)
  close()
}

function onDocClick(e) {
  const target = e.target
  if (root.value?.contains(target)) return
  if (popRef.value?.contains(target)) return
  close()
}

function updatePopPosition() {
  const btn = btnRef.value
  if (!btn || !open.value) return

  const rect = btn.getBoundingClientRect()
  const gap = 8
  const edge = 16
  const minList = 120
  const preferList = 280
  const searchH = props.searchable ? 52 : 0
  const popPad = 16

  const spaceBelow = window.innerHeight - rect.bottom - gap - edge
  const spaceAbove = rect.top - gap - edge
  const preferTotal = preferList + searchH + popPad

  let upward = false
  let available = spaceBelow
  if (spaceBelow < preferTotal && spaceAbove > spaceBelow) {
    upward = true
    available = spaceAbove
  }

  openUp.value = upward
  listMaxHeight.value = Math.max(minList, Math.min(preferList, available - searchH - popPad))

  const width = Math.min(rect.width, window.innerWidth - edge * 2)
  const left = Math.min(Math.max(edge, rect.left), window.innerWidth - width - edge)

  if (upward) {
    popStyle.value = {
      left: `${left}px`,
      width: `${width}px`,
      bottom: `${window.innerHeight - rect.top + gap}px`,
      top: 'auto',
    }
  } else {
    popStyle.value = {
      left: `${left}px`,
      width: `${width}px`,
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
    }
  }
}

watch(open, async (v) => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('scroll', updatePopPosition, true)
  window.removeEventListener('resize', updatePopPosition)
  if (!v) return
  await nextTick()
  if (!open.value) return
  updatePopPosition()
  document.addEventListener('click', onDocClick)
  window.addEventListener('scroll', updatePopPosition, true)
  window.addEventListener('resize', updatePopPosition)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('scroll', updatePopPosition, true)
  window.removeEventListener('resize', updatePopPosition)
})
</script>

<style scoped>
.fs { position: relative; width: 100%; }
.fs.fs--sm {
  width: 112px;
  min-width: 112px;
  max-width: 112px;
  flex-shrink: 0;
}
.fs.disabled { opacity: .65; pointer-events: none; }
.fs.open .fs-btn { border-color: #9bb7ff; box-shadow: 0 6px 18px rgba(47,106,246,.08); }
.fs-btn{
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  cursor: pointer;
}
.fs-btn:hover{ border-color:#c5d3ee; box-shadow:0 6px 18px rgba(47,106,246,.08); }
.fs-icon{ opacity:.8; }
.fs-text{ flex:1; text-align:left; color:#1d2939; font-size:13px; }
.fs-text.placeholder{ color:#98a2b3; }
.fs-chevron{ color:#667085; font-size:14px; }
.fs.open .fs-chevron { transform: rotate(180deg); }
.fs-chevron { transition: transform .15s ease; }

.fs-pop--fixed{
  position: fixed;
  background:#fff;
  border:1px solid #e9eef5;
  border-radius:12px;
  box-shadow:0 18px 60px rgba(15,23,42,.18);
  padding:8px;
  z-index: 3000;
}
.fs-pop-enter-active{
  transition: opacity .16s ease, transform .16s cubic-bezier(.22,.61,.36,1);
  transform-origin: top;
}
.fs-pop-leave-active{
  transition: opacity .12s ease, transform .12s ease;
  transform-origin: top;
}
.fs-pop-enter-from,
.fs-pop-leave-to{
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
.fs-pop-enter-to,
.fs-pop-leave-from{
  opacity: 1;
  transform: translateY(0) scale(1);
}
.fs-pop-up-enter-active{
  transition: opacity .16s ease, transform .16s cubic-bezier(.22,.61,.36,1);
  transform-origin: bottom;
}
.fs-pop-up-leave-active{
  transition: opacity .12s ease, transform .12s ease;
  transform-origin: bottom;
}
.fs-pop-up-enter-from,
.fs-pop-up-leave-to{
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}
.fs-pop-up-enter-to,
.fs-pop-up-leave-from{
  opacity: 1;
  transform: translateY(0) scale(1);
}
.fs-search{
  display:flex;
  align-items:center;
  gap:8px;
  border:1px solid #eef2f7;
  background:#fbfcff;
  border-radius:10px;
  padding:8px 10px;
  margin-bottom:8px;
}
.fs-search-ico{ color:#667085; }
.fs-search-input{
  border:none;
  outline:none;
  background:transparent;
  flex:1;
}
.fs-list{
  overflow:auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
.fs-list::-webkit-scrollbar{ width: 8px; }
.fs-list::-webkit-scrollbar-track{ background: transparent; }
.fs-list::-webkit-scrollbar-thumb{
  background: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.7);
}
.fs-list::-webkit-scrollbar-thumb:hover{ background: rgba(15, 23, 42, 0.24); }
.fs-item{
  width:100%;
  border:1px solid transparent;
  background:#fff;
  border-radius:10px;
  padding:9px 10px;
  display:flex;
  justify-content:space-between;
  align-items:center;
  cursor:pointer;
  text-align:left;
  transition: background .12s ease, border-color .12s ease, transform .12s ease;
}
.fs-item:hover{ background:#f5f8ff; border-color:#d8e4ff; }
.fs-item.active{ background:#2f6af6; color:#fff; }
.fs-item.active .fs-item-value{ color: rgba(255,255,255,.9); }
.fs-item.disabled,
.fs-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}
.fs-item-label{ font-weight:600; font-size:13px; }
.fs-item-value{ font-size:12px; color:#667085; margin-left:10px; }
.fs-empty{ padding:10px; color:#667085; font-size:13px; }

/* 紧凑版：分页区等，对齐 btn-edit btn-sm */
.fs--sm .fs-btn {
  width: 100%;
  height: auto;
  min-height: 0;
  padding: 5px 10px;
  border-radius: 8px;
  border-color: #bfc9db;
  box-shadow: none;
  white-space: nowrap;
}
.fs--sm .fs-btn:hover {
  border-color: #98a2b3;
  box-shadow: none;
  background: #f9fafb;
}
.fs--sm.open .fs-btn {
  border-color: #9bb7ff;
  box-shadow: 0 6px 18px rgba(47, 106, 246, 0.08);
}
.fs--sm .fs-text {
  font-size: 12px;
  color: #26457d;
}
.fs--sm .fs-chevron {
  font-size: 12px;
}
</style>
